<template>
  <div>
    <!-- Stats -->
    <div class="wa-stats-grid mb-4">
      <div class="wa-stat-card">
        <div class="wa-stat-card__value">{{ stats.total_sent || 0 }}</div>
        <div class="wa-stat-card__label">Enviados (mes)</div>
      </div>
      <div class="wa-stat-card">
        <div class="wa-stat-card__value" style="color:#3ecfb4;">
          {{ stats.delivery_rate || 0 }}%
        </div>
        <div class="wa-stat-card__label">Tasa de entrega</div>
      </div>
      <div class="wa-stat-card">
        <div class="wa-stat-card__value" style="color:#2d2b55;">
          {{ stats.read_rate || 0 }}%
        </div>
        <div class="wa-stat-card__label">Tasa de lectura</div>
      </div>
      <div class="wa-stat-card">
        <div class="wa-stat-card__value" style="color:#6b7280;">
          {{ stats.total_replies || 0 }}
        </div>
        <div class="wa-stat-card__label">Respuestas</div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="d-flex align-items-center mb-3" style="gap:8px;flex-wrap:wrap;">
      <el-select
        v-model="filters.status"
        placeholder="Estado"
        clearable
        size="small"
        style="width:140px;"
        @change="load"
      >
        <el-option label="Enviado" value="sent" />
        <el-option label="Entregado" value="delivered" />
        <el-option label="Leído" value="read" />
        <el-option label="Fallido" value="failed" />
      </el-select>
      <el-date-picker
        v-model="filters.dateRange"
        type="daterange"
        size="small"
        range-separator="–"
        start-placeholder="Desde"
        end-placeholder="Hasta"
        value-format="yyyy-MM-dd"
        style="width:240px;"
        @change="load"
      />
      <el-button size="small" icon="el-icon-refresh" @click="load"
        >Actualizar</el-button
      >
    </div>

    <!-- Lista vacía -->
    <div v-if="!loading && !messages.length" class="wa-empty">
      <div style="font-size:32px;">💬</div>
      <div class="mt-2" style="font-weight:600;color:#2d2b55;">
        Sin mensajes aún
      </div>
      <div style="font-size:13px;color:#6b7280;margin-top:4px;">
        Los mensajes enviados por WhatsApp aparecerán aquí con su estado de
        entrega.
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-4">
      <i class="el-icon-loading" style="font-size:24px;color:#3ecfb4;"></i>
    </div>

    <!-- Mensajes -->
    <div v-for="msg in messages" :key="msg.id" class="wa-msg-row mb-2">
      <!-- Avatar -->
      <div class="wa-msg-row__avatar">
        {{ initials(msg.client ? msg.client.name : msg.recipient_name) }}
      </div>

      <!-- Info principal -->
      <div class="wa-msg-row__info">
        <div class="wa-msg-row__name">
          {{
            msg.client
              ? msg.client.name
              : msg.recipient_name || msg.recipient_phone
          }}
        </div>
        <div class="wa-msg-row__meta">
          <span>{{ msg.template_name }}</span>
          <span class="wa-dot">·</span>
          <span>{{ formatDate(msg.created_at) }}</span>
        </div>
        <!-- Error message -->
        <div
          v-if="msg.status === 'failed' && msg.error_message"
          class="wa-msg-row__error mt-1"
        >
          {{ msg.error_message }}
        </div>
      </div>

      <!-- Status -->
      <div class="wa-msg-row__status ml-auto">
        <WhatsappStatusBadge :status="msg.status" />
        <div v-if="msg.direction === 'inbound'" class="wa-replied-badge mt-1">
          Respondió
        </div>
      </div>
    </div>

    <!-- Paginación -->
    <div
      v-if="pagination && pagination.last_page > 1"
      class="d-flex justify-content-center mt-3"
    >
      <el-pagination
        :current-page="pagination.current_page"
        :page-size="pagination.per_page"
        :total="pagination.total"
        layout="prev, pager, next"
        small
        @current-change="changePage"
      />
    </div>
  </div>
</template>

<script>
import configService from "../../../services/configService";
import WhatsappStatusBadge from "./WhatsappStatusBadge";

export default {
  name: "WhatsappMessageLog",
  components: { WhatsappStatusBadge },
  data() {
    return {
      messages: [],
      pagination: null,
      stats: {},
      loading: false,
      filters: { status: "", dateRange: null },
      page: 1
    };
  },
  mounted() {
    this.loadStats();
    this.load();
  },
  methods: {
    async loadStats() {
      try {
        const res = await configService.getWhatsAppMessageStats();
        if (res.data && res.data.success) this.stats = res.data.data;
      } catch {
        // No crítico: las stats no se cargan pero el historial sigue funcional
      }
    },

    async load() {
      this.loading = true;
      const params = { page: this.page };
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        params.date_from = this.filters.dateRange[0];
        params.date_to = this.filters.dateRange[1];
      }
      try {
        const res = await configService.getWhatsAppMessages(params);
        if (res.data.success) {
          this.messages = res.data.data.data || res.data.data;
          this.pagination = res.data.data.meta || null;
        }
      } finally {
        this.loading = false;
      }
    },

    changePage(p) {
      this.page = p;
      this.load();
    },

    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .slice(0, 2)
        .map(w => w[0])
        .join("")
        .toUpperCase();
    },

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  }
};
</script>

<style scoped>
.wa-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
@media (max-width: 640px) {
  .wa-stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.wa-stat-card {
  border: 0.5px solid #e8e7e3;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fff;
  text-align: center;
}
.wa-stat-card__value {
  font-size: 22px;
  font-weight: 700;
  color: #2d2b55;
}
.wa-stat-card__label {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.wa-msg-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border: 0.5px solid #e8e7e3;
  border-radius: 10px;
  background: #fff;
}
.wa-msg-row__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ede9fe;
  color: #6d28d9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.wa-msg-row__name {
  font-size: 13px;
  font-weight: 600;
  color: #2d2b55;
}
.wa-msg-row__meta {
  font-size: 12px;
  color: #6b7280;
}
.wa-msg-row__error {
  font-size: 12px;
  color: #dc2626;
}
.wa-msg-row__status {
  flex-shrink: 0;
  text-align: right;
}

.wa-dot {
  margin: 0 4px;
}

.wa-replied-badge {
  display: inline-block;
  background: #dbeafe;
  color: #1e40af;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
}
.wa-empty {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}
</style>
